<template>
    <div ref="trigger" @click="toggle">
        <slot name="trigger"></slot>
    </div>

    <teleport to="body">
        <div ref="popover" v-show="show" class="popover absolute z-50 text-base bg-white shadow-lg rounded-lg" :style="popoverStyles">
            <slot name="content" :close="close" />
        </div>
    </teleport>
</template>

<script setup>
import { onClickOutside } from '@vueuse/core'

const props = defineProps({
    placement: { type: String, default: 'bottom-center' }
})

const trigger = ref(null)
const popover = ref(null)
const show = ref(false)
const popoverStyles = ref({})

const toggle = async () => {
    show.value = !show.value

    if (show.value) {
        await nextTick()
        positionPopover()
    }
}

const close = () => (show.value = false)

/**
 * FULL PLACEMENT MAP
 * 12 positions (top, bottom, left, right + alignment)
 */
const placements = {
    'top-left': (t, p) => ({
        top: t.top - p.height - 10,
        left: t.left
    }),
    'top-center': (t, p) => ({
        top: t.top - p.height - 10,
        left: t.left + t.width / 2 - p.width / 2
    }),
    'top-right': (t, p) => ({
        top: t.top - p.height - 10,
        left: t.right - p.width
    }),
    'bottom-left': (t, p) => ({
        top: t.bottom + 10,
        left: t.left
    }),
    'bottom-center': (t, p) => ({
        top: t.bottom + 10,
        left: t.left + t.width / 2 - p.width / 2
    }),
    'bottom-right': (t, p) => ({
        top: t.bottom + 2,
        left: t.right - p.width
    }),
    'left-top': (t, p) => ({
        top: t.top,
        left: t.left - p.width - 10
    }),
    'left-center': (t, p) => ({
        top: t.top + t.height / 2 - p.height / 2,
        left: t.left - p.width - 10
    }),
    'left-bottom': (t, p) => ({
        top: t.bottom - p.height,
        left: t.left - p.width - 10
    }),
    'right-top': (t, p) => ({
        top: t.top,
        left: t.right + 10
    }),
    'right-center': (t, p) => ({
        top: t.top + t.height / 2 - p.height / 2,
        left: t.right + 10
    }),
    'right-bottom': (t, p) => ({
        top: t.bottom - p.height,
        left: t.right + 10
    })
}

const positionPopover = () => {
    if (!trigger.value || !popover.value) return

    const t = trigger.value.getBoundingClientRect()
    const p = popover.value.getBoundingClientRect()

    // pick placement function or fallback
    const placementFn = placements[props.placement] || placements['bottom-center']
    const base = placementFn(t, p)

    // convert viewport → document
    let top = base.top + window.scrollY
    let left = base.left + window.scrollX

    // clamp inside viewport
    top = Math.min(Math.max(window.scrollY + 10, top), window.scrollY + window.innerHeight - p.height - 10)
    left = Math.min(Math.max(window.scrollX + 10, left), window.scrollX + window.innerWidth - p.width - 10)

    popoverStyles.value = {
        top: `${top}px`,
        left: `${left}px`
    }
}

/**
 * Close when clicking outside
 */
onClickOutside(popover, () => {
    if (show.value) close()
})
</script>

<style scoped>
.popover {
    transition:
        opacity 0.15s ease,
        transform 0.15s ease;
}
</style>
